import {useCallback, useEffect} from 'react';
import {
  Database,
  GalleryVerticalEnd,
  ScanEye,
  SearchCheck,
} from 'lucide-react';
import {type StepDefinition, useJobsSimulationConfiguration} from '@tinct/web-components';
import {useGetCampaignById, usePublishCampaign} from '@tinct/core-api';
import {useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {usePublishingJobAlert} from './use-publishing-job-alert';

const POLL_INTERVAL_MS = 3000;

const publishingSteps: StepDefinition[] = [
  {title: 'Preparing your pages', icon: <GalleryVerticalEnd />, duration: 10, strategy: 'linear'},
  {title: 'Deploying to CDN', icon: <Database />, duration: 15, strategy: 'linear'},
  {title: 'Activating detection', icon: <ScanEye />, duration: 10, strategy: 'linear'},
  {title: 'Finalizing', icon: <SearchCheck />, duration: 8, strategy: 'exponentialBackoff'},
];

export function usePublishingJob(campaignId: string) {
  const workspaceId = useGetCurrentWorkspaceId();
  const {mutate: publishCampaign} = usePublishCampaign();

  const {data: campaign} = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        lastPublishedJob: {
          fields: {
            id: null,
            status: null,
          },
        },
      },
    },
    {
      // Stop polling as soon as we hit a terminal state.
      refetchInterval: (query) => {
        const status = query.state.data?.lastPublishedJob?.status;
        return status === 'completed' || status === 'failed' ? false : POLL_INTERVAL_MS;
      },
    }
  );

  const publishStatus = campaign?.lastPublishedJob?.status;
  const isPublishCompleted = publishStatus === 'completed';
  const isPublishFailed = publishStatus === 'failed';

  const {start: startJobAlert, gameModal} = usePublishingJobAlert(campaignId);

  useEffect(() => {
    if (campaign?.lastPublishedJob?.id) {
      startJobAlert(campaign.lastPublishedJob.id);
    }
  }, [campaign?.lastPublishedJob?.id, startJobAlert]);

  const {subJobs, allCompleted} = useJobsSimulationConfiguration({
    steps: publishingSteps,
    isAnalysisCompleted: isPublishCompleted,
  });

  const retryPublish = useCallback(() => {
    if (!workspaceId) return;
    publishCampaign({id: campaignId, organizationId: workspaceId});
  }, [publishCampaign, campaignId, workspaceId]);

  return {subJobs, allCompleted, isPublishFailed, retryPublish, gameModal};
}
